import React, { useEffect, useState } from 'react';
import {
  ExternalLink,
  Sun,
  Moon,
  Search as SearchIcon,
  BarChart3,
  CreditCard,
  Users,
  Building2,
  Home,
  Activity,
  Send,
  MessageSquare,
  Sliders,
  Mail,
  LogOut,
} from 'lucide-react';
import { Toaster } from 'sonner';
import { UserProfile } from '../types';
import { AdminCommandPalette, type ActionId } from './admin/AdminCommandPalette';
import type { AdminTabId } from './admin/types';
import { OverviewTab } from './admin/tabs/OverviewTab';
import { RevenueTab } from './admin/tabs/RevenueTab';
import { UsersTab } from './admin/tabs/UsersTab';
import { KantorenTab } from './admin/tabs/KantorenTab';
import { PropertiesTab } from './admin/tabs/PropertiesTab';
import { RendersTab } from './admin/tabs/RendersTab';
import { InvitesTab } from './admin/tabs/InvitesTab';
import { FeedbackTab } from './admin/tabs/FeedbackTab';
import { PreferencesTab } from './admin/tabs/PreferencesTab';
import { LogsTab } from './admin/tabs/LogsTab';
import { TabErrorBoundary } from './admin/_primitives/TabErrorBoundary';

interface Props {
  user: UserProfile;
}

type TabGroup = 'main' | 'ops' | 'meta';
const TABS: { id: AdminTabId; label: string; icon: React.ComponentType<{ size?: number }>; group: TabGroup }[] = [
  { id: 'overview', label: 'Overzicht', icon: BarChart3, group: 'main' },
  { id: 'revenue', label: 'Inkomsten', icon: CreditCard, group: 'main' },
  { id: 'users', label: 'Gebruikers', icon: Users, group: 'main' },
  { id: 'kantoren', label: 'Kantoren', icon: Building2, group: 'main' },
  { id: 'properties', label: 'Woningen', icon: Home, group: 'ops' },
  { id: 'renders', label: 'Renders', icon: Activity, group: 'ops' },
  { id: 'invites', label: 'Uitnodigingen', icon: Send, group: 'ops' },
  { id: 'feedback', label: 'Feedback', icon: MessageSquare, group: 'ops' },
  { id: 'preferences', label: 'Voorkeuren', icon: Sliders, group: 'meta' },
  { id: 'logs', label: 'Email logs', icon: Mail, group: 'meta' },
];

const THEME_STORAGE_KEY = 'stagery-admin-theme';
type Theme = 'light' | 'dark';

const SCOPE_CLASS = 'stagery-admin-scope';

/**
 * Scoped CSS — lifted from the legacy `backend/admin.html` (basgosewisch/stagery-ai)
 * so the React rewrite carries the same warm-stone + orange-accent visual identity.
 * All variables live under `.stagery-admin-scope` so the rest of the app is
 * untouched. Uses CSS custom properties so light/dark switch is a class flip
 * and child Tailwind components inherit through `var(--ink)` etc.
 */
const ADMIN_CSS = `
.${SCOPE_CLASS} {
  --bg: #fafaf9;
  --surface: #fff;
  --bg-card: #fff;
  --bg-soft: #f3f2ee;
  --bg-dark: #1c1c20;
  --bg-dark-2: #26262a;
  --stone: #f3f2ee;
  --stone-dark: #ecebe5;
  --border: rgba(0,0,0,0.07);
  --border-hover: rgba(0,0,0,0.18);
  --line: rgba(0,0,0,0.07);
  --line-soft: rgba(0,0,0,0.04);
  --ink: #1a1a1c;
  --ink-70: rgba(26,26,28,0.70);
  --ink-55: rgba(26,26,28,0.55);
  --ink-40: rgba(26,26,28,0.40);
  --accent: #f16e00;
  --accent-hover: #d86300;
  --accent-soft: rgba(241,110,0,0.10);
  --radius-sm: 8px;
  --radius-md: 10px;
  --radius-lg: 14px;
  --radius-xl: 18px;
  --shadow: 0 2px 6px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03);
  --shadow-hover: 0 12px 36px -12px rgba(26,26,24,0.14);
  font-family: 'Inter', -apple-system, 'Helvetica Neue', sans-serif;
  font-size: 13.5px;
  background: var(--bg);
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
  font-feature-settings: "cv11";
}
.${SCOPE_CLASS}.dark {
  --bg: #0f1013;
  --surface: #17181c;
  --bg-card: #17181c;
  --bg-soft: #1f2026;
  --stone: #1f2026;
  --stone-dark: #262830;
  --border: rgba(255,255,255,0.08);
  --border-hover: rgba(255,255,255,0.22);
  --line: rgba(255,255,255,0.08);
  --line-soft: rgba(255,255,255,0.04);
  --ink: #f2f3f5;
  --ink-70: rgba(242,243,245,0.72);
  --ink-55: rgba(242,243,245,0.55);
  --ink-40: rgba(242,243,245,0.40);
  --shadow: 0 2px 8px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.25);
  --shadow-hover: 0 16px 40px -12px rgba(0,0,0,0.55);
}
.${SCOPE_CLASS} .num { font-variant-numeric: tabular-nums; font-feature-settings: "tnum"; letter-spacing: -0.02em; }

/* Header (dark, full width, 64px) */
.${SCOPE_CLASS} .admin-header {
  height: 64px;
  background: var(--bg-dark);
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 22px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  position: sticky;
  top: 0;
  z-index: 30;
}
.${SCOPE_CLASS} .admin-brand { display: flex; align-items: center; gap: 10px; text-decoration: none; }
.${SCOPE_CLASS} .admin-logo {
  width: 32px; height: 32px; border-radius: 9px;
  background: var(--accent); color: #fff;
  display: grid; place-items: center;
  box-shadow: 0 2px 10px -2px rgba(241,110,0,0.45);
  font-weight: 800; font-size: 15px; letter-spacing: -0.04em;
}
.${SCOPE_CLASS} .admin-brand-name { font-size: 15px; font-weight: 700; color: #fff; letter-spacing: -0.015em; }
.${SCOPE_CLASS} .admin-brand-tag {
  margin-left: 6px; padding: 3px 9px 4px;
  background: var(--accent); color: #fff;
  border-radius: 100px;
  font-size: 9.5px; font-weight: 700; letter-spacing: 0.11em;
  text-transform: uppercase; line-height: 1.2;
  box-shadow: 0 2px 8px -2px rgba(241,110,0,0.55);
}
@media (max-width: 720px) { .${SCOPE_CLASS} .admin-brand-tag { display: none; } }
.${SCOPE_CLASS} .header-tab {
  padding: 7px 14px; border-radius: var(--radius-md);
  font-size: 13px; font-weight: 500;
  color: rgba(255,255,255,0.55);
  cursor: pointer; transition: all 0.15s;
  letter-spacing: -0.005em;
  background: none; border: none; font-family: inherit;
}
.${SCOPE_CLASS} .header-tab:hover { color: rgba(255,255,255,0.9); background: rgba(255,255,255,0.06); }
.${SCOPE_CLASS} .header-tab.active { color: #fff; background: rgba(255,255,255,0.12); }

.${SCOPE_CLASS} .header-search {
  margin-left: auto;
  display: flex; align-items: center; gap: 10px;
  min-width: 320px; height: 38px; padding: 0 6px 0 14px;
}
.${SCOPE_CLASS} .header-search.header-search-left {
  margin-left: 18px;
  margin-right: auto;
  flex: 1;
  max-width: 480px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 100px;
  color: rgba(255,255,255,0.75);
  font: inherit; cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 0 rgba(255,255,255,0.04) inset, 0 2px 10px -4px rgba(0,0,0,0.4);
}
.${SCOPE_CLASS} .header-search:hover {
  background: rgba(255,255,255,0.13);
  border-color: rgba(255,255,255,0.22);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 1px 0 rgba(255,255,255,0.06) inset, 0 6px 18px -6px rgba(0,0,0,0.5);
}
.${SCOPE_CLASS} .header-search-text { flex: 1; text-align: left; font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.7); }
.${SCOPE_CLASS} .header-search-kbd { display: inline-flex; gap: 2px; }
.${SCOPE_CLASS} .header-search-kbd kbd {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 24px; padding: 0 6px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 6px;
  font: 600 11px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
  color: rgba(255,255,255,0.85);
}
@media (max-width: 960px) {
  .${SCOPE_CLASS} .header-search { min-width: 0; width: 42px; padding: 0; justify-content: center; }
  .${SCOPE_CLASS} .header-search-text, .${SCOPE_CLASS} .header-search-kbd { display: none; }
}

.${SCOPE_CLASS} .icon-btn-dark {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.85);
  width: 38px; height: 38px; border-radius: var(--radius-md);
  display: grid; place-items: center;
  cursor: pointer; transition: all 0.15s;
}
.${SCOPE_CLASS} .icon-btn-dark:hover {
  border-color: rgba(255,255,255,0.35); color: #fff;
  background: rgba(255,255,255,0.1);
}

.${SCOPE_CLASS} .admin-me {
  display: flex; align-items: center; gap: 10px;
  padding: 4px 4px 4px 12px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 100px;
}
.${SCOPE_CLASS} .admin-me-text { display: flex; flex-direction: column; line-height: 1.15; text-align: right; }
.${SCOPE_CLASS} .admin-me-name { font-size: 12.5px; font-weight: 600; color: #fff; }
.${SCOPE_CLASS} .admin-me-email {
  font-size: 10.5px; color: rgba(255,255,255,0.55);
  font-weight: 500; max-width: 180px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.${SCOPE_CLASS} .admin-me-avatar {
  width: 30px; height: 30px;
  background: var(--accent); border-radius: 50%;
  display: grid; place-items: center;
  font-size: 12px; font-weight: 700; color: #fff;
  box-shadow: 0 2px 10px -2px rgba(241,110,0,0.6);
}
@media (max-width: 720px) { .${SCOPE_CLASS} .admin-me-text { display: none; } }

/* Layout — sidebar + main */
.${SCOPE_CLASS} .admin-layout { display: flex; min-height: calc(100vh - 64px); }
.${SCOPE_CLASS} .admin-sidebar {
  width: 220px;
  background: var(--surface);
  border-right: 1px solid var(--line);
  padding: 14px 0;
  flex-shrink: 0;
  overflow-y: auto;
  position: sticky;
  top: 64px;
  height: calc(100vh - 64px);
  transition: transform 0.25s ease;
}
.${SCOPE_CLASS} .sidebar-item {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 18px;
  font-size: 13.5px; font-weight: 500;
  color: var(--ink-70);
  border: none; background: none;
  border-left: 3px solid transparent;
  cursor: pointer; transition: all 0.15s;
  letter-spacing: -0.005em;
  width: 100%; text-align: left; font-family: inherit;
  position: relative;
}
.${SCOPE_CLASS} .sidebar-item:hover { color: var(--ink); background: var(--bg-soft); }
.${SCOPE_CLASS} .sidebar-item.active {
  color: var(--ink);
  background: var(--accent-soft);
  border-left-color: var(--accent);
  font-weight: 600;
}
.${SCOPE_CLASS} .sidebar-item.active::after {
  content: ""; position: absolute;
  right: 14px; top: 50%; transform: translateY(-50%);
  width: 5px; height: 5px; border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-soft);
}

/* Mobile drawer */
.${SCOPE_CLASS} .sidebar-toggle { display: none; }
.${SCOPE_CLASS} .sidebar-backdrop { display: none; }
@media (max-width: 768px) {
  .${SCOPE_CLASS} .sidebar-toggle {
    display: grid; place-items: center;
    width: 38px; height: 38px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.12);
    color: rgba(255,255,255,0.85);
    border-radius: var(--radius-md);
    cursor: pointer;
  }
  .${SCOPE_CLASS} .admin-sidebar {
    position: fixed;
    top: 64px; left: 0;
    z-index: 40;
    transform: translateX(-100%);
    box-shadow: 0 30px 60px -20px rgba(0,0,0,0.3);
  }
  .${SCOPE_CLASS} .admin-sidebar.open { transform: translateX(0); }
  .${SCOPE_CLASS} .sidebar-backdrop {
    display: none;
    position: fixed; inset: 64px 0 0 0;
    background: rgba(0,0,0,0.4); z-index: 35;
  }
  .${SCOPE_CLASS} .sidebar-backdrop.open { display: block; }
  .${SCOPE_CLASS} .admin-main { padding: 18px 14px !important; }
  .${SCOPE_CLASS} .section-title { font-size: 22px !important; }
  .${SCOPE_CLASS} .admin-header { padding: 0 12px; gap: 8px; }
  .${SCOPE_CLASS} .admin-brand-name { display: none; }
  .${SCOPE_CLASS} .admin-brand-tag { display: none; }
}
.${SCOPE_CLASS} .sidebar-group-label {
  padding: 14px 18px 6px;
  font-size: 10.5px; font-weight: 600;
  color: var(--ink-40);
  text-transform: uppercase; letter-spacing: 0.09em;
}
.${SCOPE_CLASS} .sidebar-sep { height: 1px; background: var(--line); margin: 10px 18px; }
.${SCOPE_CLASS} .sidebar-foot {
  margin-top: auto; padding: 14px 18px;
  font-size: 11px; color: var(--ink-40);
  border-top: 1px solid var(--line);
  display: flex; gap: 10px; align-items: center;
}
.${SCOPE_CLASS} .sidebar-foot a {
  color: var(--ink-55); text-decoration: none;
  display: inline-flex; align-items: center; gap: 4px;
  transition: color 0.15s;
}
.${SCOPE_CLASS} .sidebar-foot a:hover { color: var(--accent); }

.${SCOPE_CLASS} .admin-main {
  flex: 1;
  padding: 28px 32px;
  overflow-y: auto;
  background: var(--bg);
}

/* Section header (eyebrow + title + lede) — marketing style */
.${SCOPE_CLASS} .eyebrow {
  display: inline-flex; align-items: center; gap: 10px;
  font-size: 11.5px; letter-spacing: 0.12em;
  text-transform: uppercase; color: var(--ink-55);
  font-weight: 600; margin-bottom: 10px;
}
.${SCOPE_CLASS} .eyebrow::before {
  content: ""; width: 14px; height: 1.5px;
  background: var(--accent); border-radius: 2px;
}
.${SCOPE_CLASS} .section-title {
  font-size: 26px; font-weight: 700;
  letter-spacing: -0.025em; color: var(--ink);
  margin-bottom: 6px;
}
.${SCOPE_CLASS} .section-lede {
  font-size: 14px; color: var(--ink-70);
  line-height: 1.6; max-width: 640px;
  margin-bottom: 24px;
}

/* Tables — apply warm-stone treatment to every DataTable inside the scope.
 * Targets element selectors (low specificity) so Tailwind utilities still win
 * where intentionally set, but baseline look matches the legacy admin. */
.${SCOPE_CLASS} table { border-collapse: separate; border-spacing: 0; }
.${SCOPE_CLASS} thead th {
  background: var(--bg-soft);
  color: var(--ink-55);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid var(--line);
}
.${SCOPE_CLASS} tbody td {
  font-size: 13.5px;
  color: var(--ink);
  border-bottom: 1px solid var(--line-soft);
}
.${SCOPE_CLASS} tbody tr:last-child td { border-bottom: none; }
.${SCOPE_CLASS} tbody tr:hover td { background: rgba(241,110,0,0.03); }
.${SCOPE_CLASS}.dark tbody tr:hover td { background: rgba(241,110,0,0.06); }

/* Inputs — orange focus ring across the whole admin */
.${SCOPE_CLASS} input[type="text"],
.${SCOPE_CLASS} input[type="search"],
.${SCOPE_CLASS} input[type="email"],
.${SCOPE_CLASS} input[type="number"],
.${SCOPE_CLASS} select {
  background: var(--surface);
  color: var(--ink);
}
.${SCOPE_CLASS} input:focus,
.${SCOPE_CLASS} select:focus {
  outline: none;
  border-color: var(--accent) !important;
  box-shadow: 0 0 0 3px var(--accent-soft);
}

/* Tailwind utility overrides — re-skin every card inside the scope without
 * editing each tab. Higher specificity (.scope .util) beats raw .util. */
.${SCOPE_CLASS} .bg-white { background: var(--surface) !important; }
.${SCOPE_CLASS}.dark .dark\\:bg-ink\\/40 { background: var(--surface) !important; }
.${SCOPE_CLASS} .border-black\\/\\[0\\.06\\],
.${SCOPE_CLASS} .border-black\\/8,
.${SCOPE_CLASS} .border-black\\/10 { border-color: var(--line) !important; }
.${SCOPE_CLASS}.dark .dark\\:border-white\\/10,
.${SCOPE_CLASS}.dark .dark\\:border-white\\/15 { border-color: var(--line) !important; }
.${SCOPE_CLASS} .text-ink { color: var(--ink) !important; }
.${SCOPE_CLASS} .text-ink\\/85 { color: var(--ink); opacity: 0.85; }
.${SCOPE_CLASS} .text-ink\\/75 { color: var(--ink); opacity: 0.75; }
.${SCOPE_CLASS} .text-ink\\/65 { color: var(--ink-70) !important; }
.${SCOPE_CLASS} .text-ink\\/55 { color: var(--ink-55) !important; }
.${SCOPE_CLASS} .text-ink\\/45,
.${SCOPE_CLASS} .text-ink\\/40 { color: var(--ink-40) !important; }
.${SCOPE_CLASS} .text-accent { color: var(--accent) !important; }
.${SCOPE_CLASS} .bg-accent { background: var(--accent) !important; }
.${SCOPE_CLASS} .border-accent { border-color: var(--accent) !important; }
.${SCOPE_CLASS} .rounded-xl { border-radius: var(--radius-xl) !important; }
.${SCOPE_CLASS} .rounded-lg { border-radius: var(--radius-md) !important; }

/* Card hover lift for any rounded-xl container */
.${SCOPE_CLASS} .stat-card,
.${SCOPE_CLASS} .rev-tile {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  padding: 20px;
  box-shadow: var(--shadow);
  transition: box-shadow 0.2s, transform 0.2s;
  position: relative;
  overflow: hidden;
}
.${SCOPE_CLASS} .stat-card:hover,
.${SCOPE_CLASS} .rev-tile:hover {
  box-shadow: var(--shadow-hover);
  transform: translateY(-1px);
}

/* Sticky table header */
.${SCOPE_CLASS} thead { position: sticky; top: 0; z-index: 5; }

/* Smooth theme transition */
.${SCOPE_CLASS},
.${SCOPE_CLASS} * { transition-property: background-color, border-color, color; transition-duration: 0.2s; }

/* Health strip — operator-grade status pills on Overview top */
.${SCOPE_CLASS} .health-strip {
  display: flex; gap: 10px; flex-wrap: wrap;
  padding: 10px 14px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow);
  margin-bottom: 16px;
}
.${SCOPE_CLASS} .health-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px;
  font-size: 11.5px; font-weight: 600;
  border-radius: 100px;
  background: var(--bg-soft);
  color: var(--ink-70);
  border: 1px solid transparent;
}
.${SCOPE_CLASS} .health-pill .dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #16a34a;
  box-shadow: 0 0 0 3px rgba(22,163,74,0.15);
}
.${SCOPE_CLASS} .health-pill.warn { color: #92400e; background: #fef3c7; border-color: #fde68a; }
.${SCOPE_CLASS} .health-pill.warn .dot { background: #d97706; box-shadow: 0 0 0 3px rgba(217,119,6,0.18); }
.${SCOPE_CLASS} .health-pill.bad { color: #991b1b; background: #fee2e2; border-color: #fecaca; }
.${SCOPE_CLASS} .health-pill.bad .dot { background: #dc2626; box-shadow: 0 0 0 3px rgba(220,38,38,0.18); }
.${SCOPE_CLASS} .health-meta {
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11px; color: var(--ink-40);
}

/* Trend chip — for stat cards */
.${SCOPE_CLASS} .trend {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 11.5px; font-weight: 600;
  padding: 2px 7px;
  border-radius: 100px;
  background: var(--bg-soft);
  color: var(--ink-55);
  margin-top: 8px;
}
.${SCOPE_CLASS} .trend.up { background: rgba(22,163,74,0.10); color: #15803d; }
.${SCOPE_CLASS} .trend.down { background: rgba(220,38,38,0.08); color: #b91c1c; }
.${SCOPE_CLASS} .trend.flat { background: var(--bg-soft); color: var(--ink-55); }

/* Sparkline */
.${SCOPE_CLASS} .sparkline {
  width: 100%; height: 32px;
  margin-top: 10px; opacity: 0.85;
}

/* Empty state action button */
.${SCOPE_CLASS} .btn-accent {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px;
  background: var(--accent); color: #fff;
  border: none; border-radius: var(--radius-md);
  font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
  font-family: inherit;
}
.${SCOPE_CLASS} .btn-accent:hover {
  background: var(--accent-hover);
  box-shadow: 0 6px 20px rgba(241,110,0,0.35);
  transform: translateY(-1px);
}

/* Filter chips */
.${SCOPE_CLASS} .chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px;
  font-size: 12px; font-weight: 500;
  border-radius: 100px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink-70);
  cursor: pointer; transition: all 0.15s;
  font-family: inherit;
}
.${SCOPE_CLASS} .chip:hover { border-color: var(--border-hover); color: var(--ink); }
.${SCOPE_CLASS} .chip.active {
  background: var(--accent-soft);
  border-color: var(--accent);
  color: var(--accent);
}

/* Keyboard shortcut hint pill (bottom-right) */
.${SCOPE_CLASS} .kbd-hint {
  position: fixed; bottom: 14px; right: 14px;
  z-index: 20;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 10px;
  font-size: 11px; font-weight: 500;
  background: rgba(28,28,32,0.9); color: rgba(255,255,255,0.85);
  border-radius: 100px;
  backdrop-filter: blur(8px);
  cursor: pointer; transition: opacity 0.2s;
}
.${SCOPE_CLASS} .kbd-hint:hover { opacity: 1; }
.${SCOPE_CLASS} .kbd-hint kbd {
  background: rgba(255,255,255,0.15);
  border-radius: 4px;
  padding: 1px 5px;
  font: 600 10px/1 ui-monospace, Menlo, monospace;
}
@media (max-width: 768px) { .${SCOPE_CLASS} .kbd-hint { display: none; } }
`;

export const AdminConsole: React.FC<Props> = ({ user }) => {
  // Tab from URL hash so deep-links survive page reloads.
  const [tab, setTab] = useState<AdminTabId>(() => {
    if (typeof window === 'undefined') return 'overview';
    const hash = window.location.hash.replace('#', '') as AdminTabId;
    return TABS.some((t) => t.id === hash) ? hash : 'overview';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onHashChange = () => {
      const hash = window.location.hash.replace('#', '') as AdminTabId;
      if (TABS.some((t) => t.id === hash)) setTab(hash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const setTabAndHash = (id: AdminTabId) => {
    setTab(id);
    if (typeof window !== 'undefined') window.location.hash = id;
  };

  // ⌘K palette state.
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Cross-tab action triggers — palette → active tab opens the right dialog.
  const [usersTrigger, setUsersTrigger] = useState<{ kind: 'grant-credits' | 'set-role' | 'impersonate' | 'invite-user' | 'merge-duplicate' | 'bulk-delete' | null; nonce: number }>({ kind: null, nonce: 0 });
  const [invitesTrigger, setInvitesTrigger] = useState<{ kind: 'create-code' | 'revoke-code' | null; nonce: number }>({ kind: null, nonce: 0 });
  const [prefsTrigger, setPrefsTrigger] = useState<{ kind: 'reset-preferences' | null; nonce: number }>({ kind: null, nonce: 0 });
  const [refreshNonce, setRefreshNonce] = useState(0);

  const onPaletteAction = (id: ActionId) => {
    switch (id) {
      case 'refresh':
        setRefreshNonce((n) => n + 1);
        return;
      case 'open-vercel': window.open('https://vercel.com/stagery', '_blank', 'noreferrer'); return;
      case 'open-sentry': window.open('https://stagery.sentry.io/projects/stagery-api/', '_blank', 'noreferrer'); return;
      case 'grant-credits':
      case 'set-role':
      case 'impersonate':
      case 'invite-user':
      case 'merge-duplicate':
      case 'bulk-delete':
        setTabAndHash('users');
        setUsersTrigger({ kind: id, nonce: Date.now() });
        return;
      case 'create-code':
      case 'revoke-code':
        setTabAndHash('invites');
        setInvitesTrigger({ kind: id, nonce: Date.now() });
        return;
      case 'reset-preferences':
        setTabAndHash('preferences');
        setPrefsTrigger({ kind: id, nonce: Date.now() });
        return;
    }
  };

  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'light';
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'));

  // ⌘K opens palette globally inside the console.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const initials = (user.firstName?.[0] ?? user.email[0] ?? '?').toUpperCase();
  const groups: TabGroup[] = ['main', 'ops', 'meta'];
  const groupLabels: Record<TabGroup, string> = { main: 'Overzicht', ops: 'Operatie', meta: 'Meta' };

  // Mobile drawer state.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Close drawer on tab switch (mobile only — desktop drawer is always open).
  const navigateTab = (id: AdminTabId) => {
    setTabAndHash(id);
    setDrawerOpen(false);
  };

  return (
    <div className={`${SCOPE_CLASS} ${theme === 'dark' ? 'dark' : ''}`}>
      <style>{ADMIN_CSS}</style>
      <Toaster richColors closeButton position="bottom-right" theme={theme} />

      <header className="admin-header">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => setDrawerOpen((o) => !o)}
          aria-label="Menu"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        <a href="/beheerconsole" className="admin-brand">
          <div className="admin-logo">
            <svg width="18" height="18" viewBox="0 0 150.015 160.089" fill="#fff" aria-hidden="true">
              <g transform="translate(-0.014423,160) scale(0.1,-0.1)">
                <path d="M382 1575 c-324 -104 -477 -479 -320 -779 49 -93 156 -195 253 -241 44 -21 81 -39 83 -41 2 -1 -64 -118 -147 -259 l-150 -256 487 3 487 3 60 24 c324 130 457 479 298 783 -48 93 -151 189 -252 236 -42 19 -77 37 -79 38 -2 1 64 117 146 258 l150 256 -471 0 c-469 -1 -472 -1 -545 -25z" />
              </g>
            </svg>
          </div>
          <span className="admin-brand-name">Stagery</span>
          <span className="admin-brand-tag">Beheerconsole</span>
        </a>

        <button type="button" onClick={() => setPaletteOpen(true)} className="header-search header-search-left" aria-label="Zoek of voer uit">
          <SearchIcon size={14} />
          <span className="header-search-text">Zoek of voer uit…</span>
          <span className="header-search-kbd">
            <kbd>⌘</kbd>
            <kbd>K</kbd>
          </span>
        </button>

        <button type="button" onClick={toggleTheme} className="icon-btn-dark" aria-label={theme === 'light' ? 'Donkere modus' : 'Lichte modus'}>
          {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
        </button>

        <div className="admin-me">
          <div className="admin-me-text">
            <span className="admin-me-name">{user.firstName || 'Admin'}</span>
            <span className="admin-me-email">{user.email}</span>
          </div>
          <div className="admin-me-avatar">{initials}</div>
        </div>

        <a href="/" className="icon-btn-dark" title="Terug naar app" aria-label="Terug naar app">
          <LogOut size={15} />
        </a>
      </header>

      <div className="admin-layout">
        <div className={`sidebar-backdrop ${drawerOpen ? 'open' : ''}`} onClick={() => setDrawerOpen(false)} />
        <aside className={`admin-sidebar flex flex-col ${drawerOpen ? 'open' : ''}`}>
          {groups.map((g, gi) => (
            <React.Fragment key={g}>
              <div className="sidebar-group-label">{groupLabels[g]}</div>
              {TABS.filter((t) => t.group === g).map((t) => {
                const Icon = t.icon;
                const active = t.id === tab;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => navigateTab(t.id)}
                    className={`sidebar-item ${active ? 'active' : ''}`}
                  >
                    <Icon size={15} />
                    <span>{t.label}</span>
                  </button>
                );
              })}
              {gi < groups.length - 1 && <div className="sidebar-sep" />}
            </React.Fragment>
          ))}

          <div className="sidebar-foot">
            <a href="https://vercel.com/stagery" target="_blank" rel="noreferrer">
              Vercel <ExternalLink size={11} />
            </a>
            <span aria-hidden style={{ opacity: 0.4 }}>·</span>
            <a href="https://stagery.sentry.io/projects/stagery-api/" target="_blank" rel="noreferrer">
              Sentry <ExternalLink size={11} />
            </a>
          </div>
        </aside>

        <main className="admin-main">
          {/* Active tab eyebrow + title */}
          {(() => {
            const t = TABS.find((x) => x.id === tab);
            if (!t) return null;
            return (
              <div>
                <div className="eyebrow">Beheerconsole · {groupLabels[t.group]}</div>
                <h1 className="section-title">{t.label}</h1>
              </div>
            );
          })()}

          {/* Tab content. `key={refreshNonce}` forces re-mount on ⌘K → Refresh. */}
          <div key={refreshNonce} className="mt-4">
            <TabErrorBoundary tabId={tab}>
              {tab === 'overview' && <OverviewTab />}
              {tab === 'revenue' && <RevenueTab />}
              {tab === 'users' && <UsersTab trigger={usersTrigger} />}
              {tab === 'kantoren' && <KantorenTab />}
              {tab === 'properties' && <PropertiesTab />}
              {tab === 'renders' && <RendersTab />}
              {tab === 'invites' && <InvitesTab trigger={invitesTrigger} />}
              {tab === 'feedback' && <FeedbackTab />}
              {tab === 'preferences' && <PreferencesTab trigger={prefsTrigger} />}
              {tab === 'logs' && <LogsTab />}
            </TabErrorBoundary>
          </div>
        </main>
      </div>

      <button type="button" className="kbd-hint" onClick={() => setPaletteOpen(true)}>
        Snel zoeken: <kbd>⌘</kbd><kbd>K</kbd>
      </button>

      <AdminCommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onNavigate={setTabAndHash}
        onAction={onPaletteAction}
      />
    </div>
  );
};
